"use client";

import * as React from "react";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { triggerHaptic } from "../../lib/haptics";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

type SegmentedControlItem =
  | {
      value: string;
      label: React.ReactNode;
      disabled?: boolean;
      popoverContent?: never;
    }
  | {
      value: string;
      label: React.ReactNode;
      disabled?: boolean;
      popoverContent: React.ReactNode | ((close: () => void) => React.ReactNode);
    };

type SegmentedControlProps = {
  value: string;
  onValueChange: (value: string) => void;
  items: SegmentedControlItem[];
  className?: string;
  itemClassName?: string;
  variant?: "default" | "outline" | "table-view" | "primary";
  size?: "default" | "sm" | "lg";
  disabled?: boolean;
};

type SegmentedButtonProps = {
  active: boolean;
  disabled?: boolean;
  itemClassName?: string;
  label: React.ReactNode;
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  showChevron?: boolean;
  open?: boolean;
  variant: NonNullable<SegmentedControlProps["variant"]>;
};

const SegmentedButton = React.forwardRef<HTMLButtonElement, SegmentedButtonProps>(function SegmentedButton(
  {
    active,
    disabled = false,
    itemClassName,
    label,
    onClick,
    onKeyDown,
    showChevron = false,
    open = false,
    variant,
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type='button'
      role='tab'
      aria-selected={active}
      disabled={disabled}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={cn(
        "relative z-10 inline-flex h-[var(--seg-height)] min-w-0 items-center justify-center rounded-[var(--seg-thumb-radius)] px-3 text-sm font-medium leading-none outline-none transition-[color] duration-150 ease-out",
        "focus-visible:ring-2 focus-visible:ring-ring/50",
        "disabled:pointer-events-none disabled:opacity-50",
        active
          ? variant === "primary"
            ? "text-primary-foreground"
            : "text-foreground"
          : "text-muted-foreground hover:text-foreground",
        itemClassName,
      )}
    >
      <span className='flex min-w-0 items-center gap-1.5'>
        <span className='min-w-0 truncate'>{label}</span>
        {showChevron ? (
          <ChevronDownIcon className={cn("size-3.5 shrink-0 transition-transform duration-200", open && "rotate-180")} />
        ) : null}
      </span>
    </button>
  );
});

function segmentedTrackClasses(size: SegmentedControlProps["size"]) {
  switch (size) {
    case "lg":
      return "[--seg-track-radius:9999px] [--seg-thumb-radius:9999px] [--seg-height:var(--control-h-lg)]";
    case "default":
      return "[--seg-track-radius:9999px] [--seg-thumb-radius:9999px] [--seg-height:var(--control-h)]";
    default:
      return "[--seg-track-radius:9999px] [--seg-thumb-radius:9999px] [--seg-height:var(--control-h-sm)]";
  }
}

function segmentedPaddingClass(size: SegmentedControlProps["size"]) {
  switch (size) {
    case "lg":
      return "[--seg-inset:2px] p-[var(--seg-inset)]";
    case "default":
      return "[--seg-inset:2px] p-[var(--seg-inset)]";
    default:
      return "[--seg-inset:2px] p-[var(--seg-inset)]";
  }
}

function moveFocus(
  refs: React.MutableRefObject<Array<HTMLButtonElement | null>>,
  currentIndex: number,
  direction: 1 | -1,
) {
  const items = refs.current;
  const total = items.length;

  for (let offset = 1; offset <= total; offset += 1) {
    const nextIndex = (currentIndex + direction * offset + total) % total;
    const candidate = items[nextIndex];
    if (!candidate || candidate.disabled) continue;
    candidate.focus();
    candidate.click();
    return;
  }
}

function SegmentedControlPopoverItem({
  active,
  disabled,
  itemClassName,
  label,
  popoverContent,
  registerRef,
  onKeyDown,
  variant,
}: {
  active: boolean;
  disabled?: boolean;
  itemClassName?: string;
  label: React.ReactNode;
  popoverContent: React.ReactNode | ((close: () => void) => React.ReactNode);
  registerRef: (node: HTMLButtonElement | null) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  variant: NonNullable<SegmentedControlProps["variant"]>;
}) {
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(() => setOpen(false), []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SegmentedButton
          ref={registerRef}
          active={active}
          disabled={disabled}
          itemClassName={itemClassName}
          label={label}
          onKeyDown={onKeyDown}
          showChevron
          open={open}
          variant={variant}
        />
      </PopoverTrigger>
      <PopoverContent align='start' className='w-72 p-2'>
        {typeof popoverContent === "function" ? popoverContent(close) : popoverContent}
      </PopoverContent>
    </Popover>
  );
}

export function SegmentedControl({
  value,
  onValueChange,
  items,
  className,
  itemClassName,
  variant = "default",
  size = "sm",
  disabled = false,
}: SegmentedControlProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const itemRefCallbacks = React.useRef<Array<(node: HTMLButtonElement | null) => void>>([]);
  const [thumbStyle, setThumbStyle] = React.useState<{
    width: number;
    height: number;
    x: number;
    y: number;
    ready: boolean;
  }>({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
    ready: false,
  });

  const updateThumb = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const activeButton = itemRefs.current.find((item) => item?.getAttribute("aria-selected") === "true");
    if (!activeButton) {
      setThumbStyle((current) => (current.ready ? { width: 0, height: 0, x: 0, y: 0, ready: false } : current));
      return;
    }

    const next = {
      width: activeButton.offsetWidth,
      height: activeButton.offsetHeight,
      x: activeButton.offsetLeft,
      y: activeButton.offsetTop,
    };

    setThumbStyle((current) => {
      if (
        current.ready &&
        current.width === next.width &&
        current.height === next.height &&
        current.x === next.x &&
        current.y === next.y
      ) {
        return current;
      }

      return { ...next, ready: true };
    });
  }, []);

  const handleItemKeyDown = React.useCallback(
    (index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        moveFocus(itemRefs, index, 1);
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        moveFocus(itemRefs, index, -1);
        return;
      }

      if (event.key === "Home") {
        event.preventDefault();
        const first = itemRefs.current.find((item) => item && !item.disabled);
        first?.focus();
        first?.click();
        return;
      }

      if (event.key === "End") {
        event.preventDefault();
        const last = [...itemRefs.current].reverse().find((item) => item && !item.disabled);
        last?.focus();
        last?.click();
      }
    },
    [],
  );

  const getItemRefCallback = React.useCallback((index: number) => {
    itemRefCallbacks.current[index] ??= (node: HTMLButtonElement | null) => {
      itemRefs.current[index] = node;
    };

    return itemRefCallbacks.current[index];
  }, []);

  React.useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(updateThumb);
    return () => window.cancelAnimationFrame(frame);
  }, [items, size, updateThumb, value]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      updateThumb();
    });

    observer.observe(container);

    itemRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, [items, updateThumb]);

  return (
    <div
      ref={containerRef}
      role='tablist'
      aria-orientation='horizontal'
      className={cn(
        // Neutral (iOS-style) track is the default; the primary/filled look is opt-in via variant="primary".
        "relative inline-flex w-fit min-w-0 max-w-full items-center gap-0 overflow-hidden rounded-[var(--seg-track-radius)] border border-[color:var(--segmented-track-border)] bg-[var(--segmented-track-bg)] shadow-none",
        variant === "primary" &&
          "border-border bg-[var(--segmented-primary-track-bg)] shadow-[inset_0_1px_1px_rgba(0,0,0,0.04)]",
        segmentedTrackClasses(size),
        segmentedPaddingClass(size),
        className,
      )}
    >
      <span
        aria-hidden='true'
        className={cn(
          "pointer-events-none absolute top-0 left-0 z-0 rounded-[var(--seg-thumb-radius)] shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_-1px_rgba(16,24,40,0.06)] transition-[transform,width,height,opacity] duration-200 ease-out will-change-[transform,width]",
          variant === "primary"
            ? "bg-primary"
            : "bg-[var(--segmented-thumb-bg)] ring-1 ring-[color:var(--segmented-thumb-ring)]",
          thumbStyle.ready ? "opacity-100" : "opacity-0",
        )}
        style={{
          width: `${thumbStyle.width}px`,
          height: `${thumbStyle.height}px`,
          transform: `translate(${thumbStyle.x}px, ${thumbStyle.y}px)`,
        }}
      />
      {items.map((item, index) => {
        const active = item.value === value;
        const registerRef = getItemRefCallback(index);

        if ("popoverContent" in item && item.popoverContent) {
          return (
            <SegmentedControlPopoverItem
              key={item.value}
              active={active}
              disabled={disabled || item.disabled}
              itemClassName={itemClassName}
              label={item.label}
              popoverContent={item.popoverContent}
              registerRef={registerRef}
              onKeyDown={handleItemKeyDown(index)}
              variant={variant}
            />
          );
        }

        return (
          <SegmentedButton
            key={item.value}
            ref={registerRef}
            active={active}
            disabled={disabled || item.disabled}
            itemClassName={itemClassName}
            label={item.label}
            onClick={() => {
              if (item.value !== value) triggerHaptic("selection")
              onValueChange(item.value)
            }}
            onKeyDown={handleItemKeyDown(index)}
            variant={variant}
          />
        );
      })}
    </div>
  );
}
